import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { BetSlipType, Odds, TaxationModel } from '@bpos';
import { BestOddsGuaranteedInformation, BestOddsGuaranteedState, SlipState } from '@bpos/v1/my-bets';
import {
    EpcotConfig,
    MyBetsConfig,
    PriceBoostConfig,
    RacingConfig,
    Sitecore,
    SitecoreMyBetsConfig,
} from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { IntlService } from '@frontend/vanilla/core';

import { MyBetsBetSlip, MyBetsListContext, TokenType } from '../../../my-bets-base/models/my-bets-viewmodels';
import { MyBetsOddsPipe } from '../../../my-bets-base/pipes/my-bets-odds.pipe';
import { BetslipUtilService } from '../../../my-bets/services/betslip-util.service';
import { MyBetsUtilService } from '../../services/my-bets-util.service';

@Component({
    selector: 'ms-my-bets-betslip-summary',
    templateUrl: 'my-bets-betslip-summary.html',
    styleUrl: '../../styles/original-odds.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MyBetsBetslipSummaryComponent implements OnInit {
    @Input() betslip: MyBetsBetSlip;
    @Input() isEditBetHistory = false;
    @Input() isCurrentBet = false;
    @Input() myBetsListContext: MyBetsListContext;

    @Output() infoClicked = new EventEmitter<void>();
    isInfoVisible = false;

    bogInfo: BestOddsGuaranteedInformation;
    readonly isEpcotEnabled: boolean;

    isBetCardRedesigned: boolean;

    get enableSGPRepricedOdds(): boolean {
        return this.myBetsConfig.enableSGPRepricedOdds;
    }

    constructor(
        private sitecore: Sitecore,
        private myBetsConfig: MyBetsConfig,
        private moneyService: MoneyService,
        private racingConfig: RacingConfig,
        private myBetsOddsPipe: MyBetsOddsPipe,
        private betslipUtilService: BetslipUtilService,
        public priceBoostConfig: PriceBoostConfig,
        public sitecoreMyBets: SitecoreMyBetsConfig,
        private intlService: IntlService,
        private myBetsUtilService: MyBetsUtilService,
        private userSettings: SyncUserSettingsService,
        private epcotConfig: EpcotConfig,
    ) {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    ngOnInit(): void {
        this.bogInfo = this.betslip.bestOddsGuaranteeInformation;
        this.isBetCardRedesigned = this.myBetsUtilService.isBetCardRedesigned();
    }

    toggleDetails(): void {
        this.infoClicked.emit();
    }

    get dateLabel(): string {
        return this.sitecore.myBets_Print.BetPlacedDateLabel;
    }

    get dateValue(): Date {
        return this.betslip.betPlacedDate;
    }

    get stakeLabel(): string {
        if (this.betslip.isFreeBet) {
            return this.isBetCardRedesigned ? this.sitecoreMyBets.myBets.Stake : this.sitecoreMyBets.myBets.FreeStake;
        } else {
            return this.stakeLabelTaxApplied;
        }
    }

    get stakeValue(): string {
        return this.moneyService.transform(this.betslip.stake.value, true);
    }

    get totalOddsLabel(): string {
        if (this.oddsAreBoosted) {
            return this.sitecore.crmTokens.myBets.BoostedOdds;
        }
        if (this.betslip.betType === BetSlipType.Single || this.betslip.isTeaserBet) {
            return this.sitecoreMyBets.myBets.Odds;
        }

        return this.sitecoreMyBets.myBets.TotalOddsNew;
    }

    get totalOddsValue(): string {
        const isStartingPrice = this.betslip.bets.some((bet) => bet.participantInfo?.isStartingPrice || bet.optionMarketInfo?.isStartingPrice);
        if (isStartingPrice) {
            return 'SP';
        }

        if (!this.betslip.totalOdds) {
            return '-';
        }

        return this.myBetsOddsPipe.transform(this.totalOdds);
    }

    get isPriceBoost(): boolean {
        return this.betslip.betType === BetSlipType.Single && this.betslip.bets[0].isPriceBoosted;
    }

    get priceBoostOriginalOdds(): string {
        if (this.betslip.betType === BetSlipType.Single) {
            return this.myBetsOddsPipe.transform(this.betslip.bets[0].priceBoostOriginalOdds);
        }

        return '';
    }

    private get totalOdds(): Odds | undefined {
        /**
         * Per BPOS spec single-bet odds can have higher precision than totalOdds (3 decimal places)
         */
        if (this.betslip.betType === BetSlipType.Single) {
            return this.betslip.bets[0]?.odds;
        }

        return this.betslip.totalOdds;
    }

    get stakeLabelTaxApplied(): string {
        return this.userSettings.isTurnoverTaxApplied ? this.sitecoreMyBets.myBets.StakeNet : this.sitecoreMyBets.myBets.Stake;
    }

    get boostedOdds(): string {
        return this.myBetsOddsPipe.transform(this.betslip.usedRewardToken?.additionalInformation?.boostedNativeOdds);
    }

    get isTurnoverTax(): boolean {
        return this.userSettings.isTurnoverTaxApplied;
    }

    get oddsAreBoosted(): boolean {
        return this.betslipUtilService.oddsAreBoosted(this.betslip);
    }

    get accaBoostUsed(): boolean {
        return this.betslipUtilService.isAccaBoostInfoIncluded(this.betslip, this.myBetsListContext);
    }

    get shouldShowRiskFreeInfoIcon(): boolean {
        return !!(this.betslip.usedRewardToken && this.betslip.usedRewardToken.tokenType === TokenType.RiskFreeToken);
    }

    get shouldShowOddsBoostInfoIcon(): boolean {
        return this.oddsAreBoosted && this.betslip.state !== SlipState.Canceled && !this.betslip.isCachedOut;
    }

    get possibleWinningsLabel(): string {
        if (this.isEditBetHistory && !this.isCurrentBet) {
            return this.sitecoreMyBets.myBets.EditBetHistoryReturn;
        }
        if (this.betslip.isCachedOut) {
            return this.sitecoreMyBets.myBets.CashedOutWinnings;
        }

        const tax = this.betslip.taxInformation.taxes.find((s) => s.model === TaxationModel.OnWinnings)?.amount;
        if (this.myBetsConfig.showNetWinnings && !!tax) {
            if (this.betslip.state === SlipState.Open) {
                return this.sitecoreMyBets.myBets.PossibleNetWinnings;
            }

            return this.sitecoreMyBets.myBets.NetWinnings;
        }

        if (this.accaBoostUsed) {
            return this.sitecoreMyBets.myBets.BoostedWinnings;
        }

        if (this.betslip.state === SlipState.Open) {
            return this.sitecoreMyBets.myBets.PossibleWinnings;
        }

        return this.sitecoreMyBets.myBets.Winnings;
    }

    get returnLabel(): string {
        return this.betslip.state === SlipState.Open ? this.sitecoreMyBets.myBets.PotentialWinnings : this.sitecoreMyBets.myBets.Payout;
    }

    get possibleWinningsValue(): string {
        return this.betslipUtilService.possibleWinningsValue(this.betslip);
    }

    get boostedWinningsValue(): string {
        return this.betslipUtilService.boostedWinningsValue(this.betslip);
    }

    get isWon(): boolean {
        return !this.isEditBetHistory && (this.betslip.isCachedOut || this.betslip.state === SlipState.Won || this.betslip.isRefunded);
    }

    get isBOGVisible(): boolean {
        let isBOGPick = true;
        this.betslip.bets.forEach((bet) => {
            const startTime = this.intlService.formatDate(bet.eventDate, 'HH:mm');
            if (
                (bet.participantInfo?.bestOddsGuaranteedState === BestOddsGuaranteedState.Eligible ||
                    bet.optionMarketInfo?.bestOddsGuaranteedState === BestOddsGuaranteedState.Eligible) &&
                startTime <= this.racingConfig.bogTodayThresholdTime
            ) {
                isBOGPick = false;

                return;
            }
        });

        return this.racingConfig.isBOGEnabled && this.bogInfo.state !== BestOddsGuaranteedState.NotEligible && isBOGPick;
    }

    get isBOGPaidOut(): boolean {
        return this.bogInfo.state === BestOddsGuaranteedState.PaidOut;
    }

    get isBOGNotPaidOut(): boolean {
        return this.bogInfo.state === BestOddsGuaranteedState.NotPaidOut;
    }

    get fixedPriceWinnings(): string {
        const winnings = this.bogInfo.fixedPriceWinnings;

        return this.moneyService.transform(winnings.value);
    }

    get hasFixedPriceWinnings(): boolean {
        return this.bogInfo.fixedPriceWinnings.value > 0;
    }

    winsInfoClick(): void {
        this.isInfoVisible = true;
    }
}
